import { useEffect, useMemo, useState } from "react";
import { BrandMark } from "../../components/BrandMark";
import {
  AdvisoryApiError,
  advisoryApi,
  isUnavailable,
  type GoalType,
  type Json,
  type ResultsBundle,
  type SessionState,
  type StatusStep,
} from "./advisoryApi";
import { dateOnly, i18n, isEditable, isRecord, money, reasonText, stepLabel, toNumber } from "./format";
import { sanitizeIconSvg } from "./svg";
import { AllocationView, CostBenefitView, ForecastView, HoldingsView, assetClassesOf } from "./steps/SnapshotViews";
import { Modal } from "./ui";

interface Requirement {
  label: string;
  where: string | null;
  reason: string | null;
}

/** Turn a 422 body (ours or QP's) into check-rows. */
function requirementsFrom(body: Record<string, unknown>): Requirement[] {
  const candidates: unknown[] = [];
  if (Array.isArray(body.open)) candidates.push(...body.open);
  const qp = isRecord(body.qp) ? body.qp : null;
  for (const k of ["requirements", "missing", "errors", "details"]) {
    if (qp && Array.isArray(qp[k])) candidates.push(...(qp[k] as unknown[]));
  }
  const out = candidates.map((c): Requirement => {
    if (typeof c === "string") return { label: c, where: null, reason: null };
    if (!isRecord(c)) return { label: String(c), where: null, reason: null };
    const step = c as unknown as StatusStep;
    const label =
      typeof c.message === "string"
        ? c.message
        : typeof c.label === "string"
          ? c.label
          : stepLabel({ ...step, kind: typeof c.kind === "string" ? c.kind : "component", status: "open" });
    return {
      label,
      where: typeof c.pageName === "string" ? c.pageName : null,
      reason: typeof c.reason === "string" ? c.reason : null,
    };
  });
  if (out.length === 0 && qp && typeof qp.error === "string") out.push({ label: qp.error, where: null, reason: null });
  return out;
}

/**
 * "Your advice" (§4.5): the recommendation per goal — risk, portfolio,
 * allocation, figures and costs — the sustainability answer and the advisor's
 * notes, with Submit gated on QP's `canSubmit`. A failed submit lists what QP
 * says is missing as check-rows.
 */
export function AdviceReview({
  state,
  onSubmitted,
  onAttempt,
  onGoTo,
}: {
  state: SessionState;
  onSubmitted: (s: SessionState) => void;
  onAttempt: () => void;
  onGoTo: (step: StatusStep) => void;
}) {
  const session = state.session;
  const status = state.status;
  const editable = isEditable(session.status);
  const [results, setResults] = useState<ResultsBundle | null>(null);
  const [resultsErr, setResultsErr] = useState<string | null>(null);
  const [types, setTypes] = useState<GoalType[]>([]);
  const [sustainability, setSustainability] = useState<boolean | null>(null);
  const [notes, setNotes] = useState<{ flowPageId: string; notes: string }[]>([]);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<Requirement[] | null>(null);

  useEffect(() => {
    const id = session.id;
    advisoryApi
      .results(id)
      .then(setResults)
      .catch((e) => setResultsErr(isUnavailable(e) ? "The recommendation can't be loaded right now." : (e as Error).message));
    advisoryApi.goalTypes(id).then((d) => setTypes(d.data)).catch(() => {});
    advisoryApi
      .loadStep<{ current: { inputData: Json | null } | null }>(id, "sustainability")
      .then((d) => {
        const v = d.data.current?.inputData?.important;
        setSustainability(typeof v === "boolean" ? v : null);
      })
      .catch(() => {});
    advisoryApi
      .loadStep<{ notes: { flowPageId: string; notes: string }[] }>(id, "advisor-note")
      .then((d) => setNotes(d.data.notes.filter((n) => n.notes.trim())))
      .catch(() => {});
  }, [session.id, status?.percent]);

  const pageNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of status?.steps ?? []) if (s.pageId && s.pageName) m.set(s.pageId, s.pageName);
    return m;
  }, [status]);

  const open = (status?.steps ?? []).filter((s) => s.status === "open");
  const canSubmit = status?.canSubmit === true;

  async function submit() {
    setSubmitting(true);
    setSubmitErr(null);
    setRequirements(null);
    try {
      const r = await advisoryApi.submit(session.id);
      setConfirm(false);
      onSubmitted(r.state);
    } catch (e) {
      setConfirm(false);
      onAttempt();
      if (e instanceof AdvisoryApiError && e.status === 422) {
        setRequirements(requirementsFrom(e.body));
        setSubmitErr(e.message);
      } else {
        setSubmitErr(isUnavailable(e) ? "The advice engine is not reachable right now — nothing was submitted. Try again in a moment." : (e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="adv-review">
      <header className="adv-review-head">
        <BrandMark size={36} />
        <div>
          <h2 className="adv-review-title">Your advice for {session.investor_name ?? "the client"}</h2>
          <p className="muted">
            {session.flow_name ?? "Advice"} · prepared by {session.advisor_username ?? "Andersen"} · {dateOnly(session.created_at)}
          </p>
        </div>
      </header>

      {editable && (
        <section className="adv-subsection">
          <span className="adv-label">Before you submit</span>
          <ul className="check-list">
            {canSubmit ? (
              <li className="check-row check-pass">
                <span className="check-icon">✓</span>
                <span>
                  <strong>Everything the advice needs is in place.</strong> Submitting locks it for the compliance review.
                </span>
              </li>
            ) : open.length === 0 ? (
              <li className="check-row adv-check-open">
                <span className="check-icon" />
                <span>Checking what is still needed…</span>
              </li>
            ) : (
              open.map((s, i) => (
                <li key={s.componentId ?? s.fieldId ?? i} className="check-row adv-check-open adv-row-link" onClick={() => onGoTo(s)}>
                  <span className="check-icon" />
                  <span>
                    <strong>{s.pageName ?? "Advice"}</strong> · {stepLabel(s)}
                    {s.reason && <span className="muted"> — {reasonText(s.reason)}</span>}
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>
      )}

      {requirements && requirements.length > 0 && (
        <section className="adv-subsection">
          <span className="adv-label">Not submitted — still needed</span>
          <ul className="check-list">
            {requirements.map((r, i) => (
              <li key={i} className="check-row check-block">
                <span className="check-icon">!</span>
                <span>
                  {r.where && <strong>{r.where} · </strong>}
                  {r.label}
                  {r.reason && <span className="muted"> — {reasonText(r.reason)}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {submitErr && !requirements?.length && <div className="banner-error">{submitErr}</div>}

      {resultsErr && <div className="banner-error">{resultsErr}</div>}
      {!results && !resultsErr && <p className="muted">Loading the recommendation…</p>}
      {results?.goals.map(({ goal, risk, portfolios }) => {
        const type = types.find((t) => t.id === goal.goalType);
        const icon = sanitizeIconSvg(type?.iconSvg);
        const r = risk?.result;
        return (
          <section key={goal.id} className="adv-goal-card">
            <div className="adv-goal-head">
              {icon && <span className="adv-goal-icon" dangerouslySetInnerHTML={{ __html: icon }} />}
              <div>
                <h3>{goal.name}</h3>
                <p className="muted">
                  {type ? i18n(type.labelI18n) : "Goal"}
                  {goal.description ? ` · ${goal.description}` : ""}
                </p>
              </div>
            </div>
            <p className="adv-reco">
              {r?.score != null ? (
                <>
                  Anders recommends a <strong>risk {r.score}</strong> portfolio
                  {r.timeHorizonName ? <> for a horizon of <strong>{r.timeHorizonName}</strong></> : null}
                  {r.riskLevelName ? <> at a <strong>{r.riskLevelName.toLowerCase()}</strong> risk level</> : null}.
                </>
              ) : (
                "The risk assessment for this goal is not complete yet."
              )}
            </p>
            {portfolios.map(({ portfolio, composition, snapshots }) => {
              const snap = (t: string) => snapshots.find((x) => x.snapshotType === t);
              const amount =
                portfolio.scheduleType === "recurring"
                  ? `${money(portfolio.recurringAmount)} ${portfolio.recurringFrequency ?? ""}`.trim()
                  : money(portfolio.oneTimeAmount);
              const alloc = snap("asset_allocation");
              const holdings = snap("portfolio");
              const forecast = snap("forecast");
              const cb = snap("cost_benefit");
              return (
                <div key={portfolio.id} className="adv-subsection">
                  <span className="adv-label">
                    {portfolio.name} ·{" "}
                    {portfolio.portfolioType === "custom"
                      ? "custom portfolio"
                      : composition?.modelPortfolioName
                        ? `model ${composition.modelPortfolioName}`
                        : "model portfolio"}{" "}
                    · {amount}
                  </span>
                  {cb && !forecast && <CostBenefitView data={cb.data} />}
                  {alloc && <AllocationView data={alloc.data} />}
                  {holdings ? (
                    <HoldingsView data={holdings.data} assetClassByIsin={assetClassesOf(composition?.positions)} />
                  ) : composition && composition.positions.length > 0 ? (
                    <HoldingsView
                      assetClassByIsin={assetClassesOf(composition.positions)}
                      data={{
                        currency: "NOK",
                        products: composition.positions.map((p) => ({ productName: p.name, isin: p.isin, weight: p.weight, amount: p.amount })),
                        totalAmount: toNumber(portfolio.oneTimeAmount),
                      }}
                    />
                  ) : (
                    <p className="muted">The holdings appear once the portfolio is resolved.</p>
                  )}
                  {forecast && <ForecastView data={forecast.data} />}
                </div>
              );
            })}
          </section>
        );
      })}

      <section className="adv-subsection">
        <span className="adv-label">Sustainability</span>
        <p>
          {sustainability === null
            ? "Not answered yet."
            : sustainability
              ? "Sustainability is important to the client."
              : "Sustainability is not a priority for the client."}
        </p>
      </section>

      {notes.length > 0 && (
        <section className="adv-subsection">
          <span className="adv-label">Advisor notes</span>
          <ul className="adv-notes-list">
            {notes.map((n, i) => (
              <li key={i}>
                <strong>{pageNames.get(n.flowPageId) ?? "Note"}</strong>
                <p>{n.notes}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {editable && (
        <div className="adv-savebar">
          <span className="muted">
            {canSubmit ? "Ready to submit." : `${Math.round(status?.percent ?? 0)} % complete — finish the open steps first.`}
          </span>
          <button type="button" className="btn btn-primary adv-btn" onClick={() => setConfirm(true)} disabled={!canSubmit || submitting}>
            Submit advice
          </button>
        </div>
      )}

      {confirm && (
        <Modal title="Submit the advice?" onClose={() => !submitting && setConfirm(false)}>
          <p>
            The advice for {session.investor_name ?? "the client"} is locked once it is submitted, and goes on to the advice document
            and the compliance review.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost adv-btn" onClick={() => setConfirm(false)} disabled={submitting}>
              Keep editing
            </button>
            <button type="button" className="btn btn-primary adv-btn" onClick={submit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit advice"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
